import {
    AnyObject,
    APIFacetField,
    FilterObject,
    RangeValue,
} from '@common/types/queryState';
import { FacetFieldConfig, FacetType, StickyBehavior } from '../types';
import { useFacetingContext } from '../context';
import { useCallback, useMemo } from 'react';

interface ValueMap {
    boolean: FilterObject[];
    range: RangeValue | undefined;
    date: RangeValue | undefined;
}

interface ChangeMap {
    boolean: FilterObject | FilterObject[];
    range: RangeValue;
    date: RangeValue;
}

type ConfigType<TType extends FacetType, TEntity> =
    | Extract<FacetFieldConfig<TEntity>, { type: TType }>
    | undefined;

export interface FacetControllerResult<TType extends FacetType, TEntity> {
    config: ConfigType<TType, TEntity>;
    apiFacet?: APIFacetField;
    realTimeFacet?: APIFacetField;
    value: ValueMap[TType];
    onChange: (val: ChangeMap[TType]) => void;
    getLabel: (val: string) => string;
}

export const useFacetController = <
    TType extends FacetType,
    TEntity extends AnyObject = AnyObject,
>(
    facetKey: string,
): FacetControllerResult<TType, TEntity> => {
    const {
        visibleConfig,
        filterState,
        getValueLabel,
        displayFacets,
        realTimeFacets,
        isStickyEnabled,
    } = useFacetingContext<TEntity>();

    const config = useMemo(
        () =>
            visibleConfig.find((c) => c.key === facetKey) as ConfigType<
                TType,
                TEntity
            >,
        [facetKey, visibleConfig],
    );

    const { apiFacet, realTimeFacet } = useMemo(() => {
        const stickyMode: StickyBehavior = config?.stickyMode || 'auto';
        const useSticky =
            stickyMode === 'always' ||
            (stickyMode === 'auto' && isStickyEnabled);

        const realTimeFacet = realTimeFacets.find((f) => f.name === facetKey);
        const apiFacet = useSticky
            ? displayFacets.find((f) => f.name === facetKey)
            : realTimeFacet;

        return { apiFacet, realTimeFacet };
    }, [config, displayFacets, facetKey, isStickyEnabled, realTimeFacets]);

    const getLabel = useCallback(
        (val: string) => getValueLabel(facetKey, val),
        [facetKey, getValueLabel],
    );

    const value = useMemo(() => {
        if (config?.type === 'range' || config?.type === 'date') {
            return filterState.selectedRanges?.[facetKey];
        }
        return filterState.selectedFacets?.[facetKey] || [];
    }, [config?.type, facetKey, filterState]) as ValueMap[TType];

    const onChange = useCallback(
        (val: unknown) => {
            if (config?.type === 'range' || config?.type === 'date') {
                filterState.setRangeFilter(facetKey, val as RangeValue);
            } else {
                filterState.toggleFacetFilter(
                    facetKey,
                    val as FilterObject | FilterObject[],
                );
            }
        },
        [config?.type, facetKey, filterState],
    ) as (val: ChangeMap[TType]) => void;

    return {
        config,
        apiFacet,
        realTimeFacet,
        value,
        onChange,
        getLabel,
    };
};
